import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface UserInput {
  id: string;
  name: string;
  type: string;
  value: string;
  placeholder?: string;
  description: string;
  validationStatus: string;
  required: boolean;
  pattern?: string;
  formAction: string;
  formMethod: string;
  website: string;
  lastUpdated: string;
  scanId: number;
  isHTTPS: boolean;
  autocomplete?: string;
}

interface UserInputsResponse {
  inputs: UserInput[];
  summary: {
    totalInputs: number;
    activeInputs: number;
    validatedInputs: number;
    pendingReview: number;
    formCount: number;
    websitesScanned: number;
  };
  lastUpdated: string;
}
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  User, 
  Mail, 
  Globe, 
  Search,
  Filter,
  Download,
  Activity,
  Shield,
  AlertTriangle,
  CheckCircle,
  Edit,
  Save,
  X
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export default function UserInputs() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "validated" | "pending" | "active">("all");
  const [editingInput, setEditingInput] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: inputsData, isLoading } = useQuery<UserInputsResponse>({
    queryKey: ["/api/user-inputs"],
    refetchInterval: 30000,
  });

  const updateInputMutation = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: string }) => {
      return apiRequest(`/api/user-inputs/${id}`, { method: "PUT", body: { value } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-inputs"] });
      setEditingInput(null);
      setEditValue("");
      toast({ title: "Input updated successfully" });
    },
    onError: () => {
      toast({ title: "Failed to update input", variant: "destructive" });
    },
  });

  const userInputs = inputsData?.inputs || [];
  const summary = inputsData?.summary || {
    totalInputs: 0,
    activeInputs: 0,
    validatedInputs: 0,
    pendingReview: 0
  };

  const getInputTypeColor = (type: string) => {
    switch (type) {
      case "url": return "bg-blue-100 text-blue-800 border-blue-200";
      case "email": return "bg-green-100 text-green-800 border-green-200";
      case "text": return "bg-slate-100 text-slate-800 border-slate-200";
      case "password": return "bg-red-100 text-red-800 border-red-200";
      case "number": return "bg-purple-100 text-purple-800 border-purple-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const getValidationColor = (status: string) => {
    switch (status) {
      case "valid": return "bg-green-100 text-green-800 border-green-200";
      case "invalid": return "bg-red-100 text-red-800 border-red-200";
      case "pending": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const filteredInputs = userInputs.filter((input: any) => {
    const matchesSearch = 
      input.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      input.value?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      input.type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || input.type === filterType;
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "validated" && input.validationStatus === "valid") ||
      (statusFilter === "pending" && input.validationStatus === "pending") ||
      (statusFilter === "active" && input.value != null && String(input.value).trim() !== "");
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleEdit = (input: any) => {
    setEditingInput(input.id);
    setEditValue(input.value || "");
  };

  const handleSave = (id: string) => {
    updateInputMutation.mutate({ id, value: editValue });
  };

  const handleCancel = () => {
    setEditingInput(null);
    setEditValue("");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            User Inputs Management
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Monitor and manage all user form inputs and data submissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/api/export/user-inputs';
              link.download = `user-inputs-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Data
          </Button>
          <Badge variant="secondary" className="text-xs">
            <Activity className="h-3 w-3 mr-1" />
            {summary.totalInputs} Inputs
          </Badge>
        </div>
      </div>

      {/* Summary Statistics - clickable to filter list */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card
          role="button"
          tabIndex={0}
          onClick={() => setStatusFilter("all")}
          onKeyDown={(e) => e.key === "Enter" && setStatusFilter("all")}
          className={`cursor-pointer transition-all hover:shadow-md hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 dark:hover:border-slate-600 ${statusFilter === "all" ? "ring-2 ring-purple-500 shadow-md border-purple-300 dark:border-purple-600" : ""}`}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.totalInputs}
                </div>
                <div className="text-xs text-slate-600">Total Inputs</div>
                <div className="text-xs text-blue-600 mt-1">
                  <Activity className="w-3 h-3 inline mr-1" />
                  All form data
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          role="button"
          tabIndex={0}
          onClick={() => setStatusFilter("validated")}
          onKeyDown={(e) => e.key === "Enter" && setStatusFilter("validated")}
          className={`cursor-pointer transition-all hover:shadow-md hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 dark:hover:border-slate-600 ${statusFilter === "validated" ? "ring-2 ring-purple-500 shadow-md border-green-300 dark:border-green-600" : ""}`}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.validatedInputs}
                </div>
                <div className="text-xs text-slate-600">Validated</div>
                <div className="text-xs text-purple-600 mt-1">
                  <Shield className="w-3 h-3 inline mr-1" />
                  Security verified
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          role="button"
          tabIndex={0}
          onClick={() => setStatusFilter("pending")}
          onKeyDown={(e) => e.key === "Enter" && setStatusFilter("pending")}
          className={`cursor-pointer transition-all hover:shadow-md hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 dark:hover:border-orange-600 ${
            statusFilter === "pending"
              ? "ring-2 ring-orange-500 shadow-md border-orange-400 dark:border-orange-600"
              : summary.pendingReview > 0
                ? "border-2 border-orange-300 bg-orange-50/50 dark:border-orange-700 dark:bg-orange-950/20"
                : ""
          }`}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.pendingReview}
                </div>
                <div className="text-xs text-slate-600">Pending Review</div>
                <div className="text-xs text-orange-600 mt-1">
                  Requires attention
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          role="button"
          tabIndex={0}
          onClick={() => setStatusFilter("active")}
          onKeyDown={(e) => e.key === "Enter" && setStatusFilter("active")}
          className={`cursor-pointer transition-all hover:shadow-md hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 dark:hover:border-slate-600 ${statusFilter === "active" ? "ring-2 ring-purple-500 shadow-md border-purple-300 dark:border-purple-600" : ""}`}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Globe className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.activeInputs}
                </div>
                <div className="text-xs text-slate-600">Active Inputs</div>
                <div className="text-xs text-purple-600 mt-1">
                  Currently processed
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search inputs by name, value, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Types</option>
            <option value="url">URLs</option>
            <option value="email">Email</option>
            <option value="text">Text</option>
            <option value="password">Password</option>
            <option value="number">Number</option>
          </select>
        </div>
      </div>

      {/* User Inputs List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-600" />
            User Input Data ({filteredInputs.length})
          </CardTitle>
          <CardDescription>
            All form inputs and user-submitted data with validation status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-slate-600">Loading user inputs...</span>
            </div>
          ) : filteredInputs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <User className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <div className="text-lg font-medium mb-2">No User Inputs Found</div>
              <div className="text-sm">
                {searchTerm || filterType !== "all"
                  ? "Try adjusting your search or filter criteria."
                  : "No user input data has been captured yet."}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredInputs.map((input: any) => {
                const needsAttention = input.validationStatus === "pending" || input.validationStatus === "invalid";
                return (
                <div
                  key={input.id}
                  className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                    needsAttention
                      ? "border-2 border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-950/30 hover:bg-orange-100 dark:hover:bg-orange-950/50"
                      : "border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }`}
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs border ${getInputTypeColor(input.type)}`}>
                        {input.type}
                      </Badge>
                      <Badge className={`text-xs border ${getValidationColor(input.validationStatus)}`}>
                        {input.validationStatus}
                      </Badge>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{input.name}</span>
                        {input.required && (
                          <Badge variant="outline" className="text-xs">Required</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 mb-2">
                        {input.description || "No description available"}
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-slate-500">Current Value:</Label>
                        {editingInput === input.id ? (
                          <div className="flex items-center gap-2">
                            {input.type === "password" ? (
                              <Input
                                type="password"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="text-xs h-8 w-64"
                              />
                            ) : input.type === "textarea" ? (
                              <Textarea
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="text-xs min-h-[60px] w-64"
                              />
                            ) : (
                              <Input
                                type={input.type === "number" ? "number" : "text"}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="text-xs h-8 w-64"
                              />
                            )}
                            <Button
                              size="sm"
                              onClick={() => handleSave(input.id)}
                              disabled={updateInputMutation.isPending}
                            >
                              <Save className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancel}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                              {input.type === "password" 
                                ? "••••••••" 
                                : input.value || "No value set"
                              }
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(input)}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Last updated: {new Date(input.lastUpdated || Date.now()).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}